import { To } from 'react-router-dom';
import { ROUTE_PATHS } from '../routes/consts';

/**
 * Creates a route for the team users page.
 * @param teamId - The ID of the team.
 * @returns The route for the team users page.
 */
export const createTeamUsersRoute = (teamId: string) =>
  `${ROUTE_PATHS.TEAM}/${teamId}/${ROUTE_PATHS.USERS}`;

/**
 * "One-shot" query params that must NOT persist across navigations: a URL
 * minter sets them deliberately for a single landing (e.g. a PR review
 * comment pointing a reviewer at one accordion section), and leaking them
 * into subsequent clicks would silently change unrelated views' default
 * behavior.
 *
 * The literals mirror `FINDING_FOCUS_QUERY_PARAM`,
 * `FINDING_ACTION_QUERY_PARAM` and `FINDING_ACTION_ORIGIN_QUERY_PARAM` in
 * `src2/components/FindingSidePanel/consts.ts` — duplicated here to keep
 * `utils/routes.ts` from depending back on `components/`.
 *
 * `action`/`origin` come from a "Dismiss in Qodo" link in a PR comment and open
 * the dismiss dialog on landing. Carrying them forward would re-open that dialog
 * on every finding the user visits next, and keep firing the return-to-git
 * navigation after each dismissal.
 */
const ONE_SHOT_QUERY_PARAMS: readonly string[] = ['focus', 'action', 'origin'];

/**
 * Overlay the target's search params on top of the current URL's, dropping
 * one-shot params from the current URL. One-shot params explicitly named in
 * the target are still honored.
 *
 * @param to - target location; a string may carry an embedded `?...` query
 * @returns merged search string with `?` prefix, or `''` if empty
 */
export const mergeSearchParams = (to: To) => {
  const currentParams = new URLSearchParams(window.location.search);

  ONE_SHOT_QUERY_PARAMS.forEach((p) => currentParams.delete(p));

  // Read the target's own search from either a string-embedded `?...` or an
  // object's `search` field. Non-string non-object falls through with no
  // overlay (carry the cleaned current params).
  let targetSearch = '';

  if (typeof to === 'string') {
    const qIdx = to.indexOf('?');

    if (qIdx >= 0) targetSearch = to.slice(qIdx);
  } else if (typeof to === 'object' && to) {
    targetSearch = to.search || '';
  }

  const newParams = new URLSearchParams(targetSearch);

  for (const [key, value] of newParams.entries()) {
    currentParams.set(key, value);
  }

  const searchString = currentParams.toString();

  return searchString ? `?${searchString}` : '';
};

/**
 * Gets the current URL (origin + pathname) encoded
 * @returns The encoded current URL
 */
export const getThisUrl = (onlyOrigin = false) =>
  encodeURIComponent(
    onlyOrigin
      ? window.location.origin
      : window.location.origin +
          window.location.pathname +
          window.location.search,
  );

/**
 * Checks whether the current pathname belongs to the billing section.
 * Compares the first path segment against the billing route.
 * @returns True if the first path segment equals the billing route
 */
export const isBillingPath = () =>
  window.location.pathname.split('/').filter(Boolean)?.[0] ===
  ROUTE_PATHS.BILLING;

/**
 * Checks whether the current pathname belongs to the onboarding section.
 * Compares the first path segment against the onboarding route.
 * @returns True if the first path segment equals the onboarding route
 */
export const isOnboardingPath = () =>
  window.location.pathname.split('/').filter(Boolean)?.[0] ===
  ROUTE_PATHS.ONBOARDING;
